import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { SmsSendResult, SmsSettings, SmsSize } from '../../api/types'
import { Card, ErrorBox, Loading } from '../../components/ui'

/**
 * Opsætning og afprøvning af SMS-gatewayen (beslutning 037). Kun Superadmin.
 *
 * API-nøglen gemmes krypteret på serveren og kan ikke hentes frem igen - feltet
 * står derfor tomt, selv om der er gemt en nøgle. Lader man det stå tomt ved et
 * gem, bliver den gemte nøgle liggende.
 */
export function SmsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => api.get<SmsSettings>('/api/sms/settings')
  })

  const [baseUrl, setBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [prefix, setPrefix] = useState('')
  const [senderName, setSenderName] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (!data) return
    setBaseUrl(data.baseUrl ?? '')
    setPrefix(data.referencePrefix)
    setSenderName(data.senderName ?? '')
    setEnabled(data.enabled)
  }, [data])

  async function save() {
    setBusy(true)
    setMessage(null)
    try {
      await api.put('/api/sms/settings', {
        baseUrl,
        apiKey,
        referencePrefix: prefix,
        senderName,
        enabled
      })
      setApiKey('')
      await queryClient.invalidateQueries({ queryKey: ['sms-settings'] })
      setMessage({ ok: true, text: 'Opsætningen er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    } finally {
      setBusy(false)
    }
  }

  async function clearKey() {
    if (!window.confirm('Fjern den gemte API-nøgle? Der kan ikke sendes SMS, før en ny er tastet ind.')) {
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      await api.delete('/api/sms/settings/api-key')
      await queryClient.invalidateQueries({ queryKey: ['sms-settings'] })
      setMessage({ ok: true, text: 'Nøglen er fjernet, og SMS er slået fra.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke fjerne nøglen.' })
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="SMS-gateway">
        <p className="small muted" style={{ marginTop: 0 }}>
          Alle SMS'er sendes gennem gatewayens REST-API - systemet taler aldrig med SMS-hardwaren.
          Gatewayen tager imod med det samme og sender fra sin egen kø, så beskeder ikke går tabt,
          hvis udstyret er nede.
        </p>

        <div className="stack" style={{ gap: 14 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Gatewayens adresse</span>
            <input
              type="url"
              placeholder="https://sms.sk-system.dk/api/v1/"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <span className="tiny muted">Skal være https - nøglen sendes med i hver forespørgsel.</span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">API-nøgle</span>
            <input
              type="password"
              autoComplete="off"
              placeholder={data?.hasApiKey ? 'Der er gemt en nøgle - lad feltet stå tomt for at beholde den' : 'sms_live_...'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <span className="tiny muted">
              Oprettes under <strong>API-klienter</strong> i gatewayen. Nøglen vises kun én gang dér.
              Den gemmes krypteret her og kan ikke hentes frem igen.
            </span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Reference</span>
            <input maxLength={20} placeholder="FLOW" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
            <span className="tiny muted">
              Sættes foran vores reference på hver besked, så de kan kendes fra andre systemers i
              gatewayens log.
            </span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Afsendernavn i beskeden</span>
            <input maxLength={30} placeholder="Flow-TC" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
            <span className="tiny muted">
              Skrives forrest i hver SMS, så modtageren kan se hvem der skriver: "Flow-TC: ...".
              Modtageren ser stadig gatewayens telefonnummer som afsender - et <strong>navn</strong> som
              afsender kræver en SMS-udbyder og kan ikke sættes herfra. Lades feltet tomt, bruges
              "Flow-TC".
            </span>
          </label>

          <label className="row" style={{ gap: 8 }}>
            {/* width: auto - ellers fylder feltet hele bredden og skubber teksten væk. */}
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Send SMS fra systemet</span>
          </label>

          <p className="tiny muted" style={{ margin: 0 }}>
            Står den fra, sendes der intet - heller ikke selvom nøglen er gemt. En SMS koster penge
            og lander hos en rigtig person, så det skal slås til bevidst.
          </p>
        </div>

        {message && (
          <div
            className={message.ok ? 'small' : 'error-box'}
            style={{ marginTop: 12, color: message.ok ? 'var(--success)' : undefined }}
          >
            {message.text}
          </div>
        )}

        <div className="btn-group" style={{ marginTop: 14 }}>
          <button className="btn btn--primary" disabled={busy} onClick={() => void save()}>
            Gem opsætning
          </button>
          {data?.hasApiKey && (
            <button className="btn btn--danger" disabled={busy} onClick={() => void clearKey()}>
              Fjern nøglen
            </button>
          )}
        </div>
      </Card>

      <TestCard configured={data?.isConfigured ?? false} enabled={data?.enabled ?? false} />
    </div>
  )
}

/** Sender en rigtig SMS for at prøve opsætningen af. */
function TestCard({ configured, enabled }: { configured: boolean; enabled: boolean }) {
  const [to, setTo] = useState('')
  const [text, setText] = useState('Testbesked fra Flow.')
  const [size, setSize] = useState<SmsSize | null>(null)
  const [result, setResult] = useState<SmsSendResult | null>(null)
  const [busy, setBusy] = useState(false)

  // Tællingen sker på serveren, så den er den samme som den, der faktisk sendes.
  // Ét emoji slår hele beskeden om til UCS-2 og halverer pladsen.
  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      api
        .post<SmsSize>('/api/sms/length', { message: text })
        .then((value) => {
          if (!cancelled) setSize(value)
        })
        .catch(() => {
          if (!cancelled) setSize(null)
        })
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [text])

  async function send() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await api.post<SmsSendResult>('/api/sms/test', { to, message: text }))
    } catch (err) {
      setResult({
        success: false,
        messageId: null,
        status: null,
        errorCode: null,
        error: err instanceof Error ? err.message : 'Kunne ikke sende.'
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Send test-SMS">
      {!configured ? (
        <p className="small muted" style={{ margin: 0 }}>
          Indtast adresse og API-nøgle ovenfor først.
        </p>
      ) : (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Beskeden går gennem den normale kø og koster som enhver anden SMS.
            {!enabled && ' SMS er slået fra lige nu, så afsendelsen vil blive afvist.'}
          </p>

          <div className="form-grid">
            <label className="stack" style={{ gap: 4 }}>
              <span className="small">Telefonnummer</span>
              <input
                type="tel"
                placeholder="22 22 55 87"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <span className="tiny muted">Otte cifre uden landekode regnes som dansk.</span>
            </label>
          </div>

          <label className="stack" style={{ gap: 4, marginTop: 4 }}>
            <span className="small">Besked</span>
            <textarea rows={3} maxLength={600} value={text} onChange={(e) => setText(e.target.value)} />
          </label>

          {size && (
            <p className="tiny muted" style={{ marginTop: 4 }}>
              {size.characters} tegn · {size.encoding} · {size.parts} SMS
              {size.parts === 1 ? '' : "'er"} · {size.remaining} tegn tilbage
              {size.encoding === 'UCS-2' && (
                <>
                  {' '}
                  — <strong>der er et tegn med, som halverer pladsen</strong> (emoji eller en
                  krøllet apostrof). Uden det er der plads til 160 tegn i stedet for 70.
                </>
              )}
            </p>
          )}

          <button
            className="btn btn--primary"
            style={{ marginTop: 12 }}
            disabled={busy || !to.trim() || !text.trim()}
            onClick={() => void send()}
          >
            {busy ? 'Sender ...' : 'Send test-SMS'}
          </button>

          {result && (
            <div
              className={result.success ? 'small' : 'error-box'}
              style={{ marginTop: 12, color: result.success ? 'var(--success)' : undefined }}
            >
              {result.success ? (
                <>
                  Beskeden er lagt i køen ({result.status}). Besked-id: <code>{result.messageId}</code>
                </>
              ) : (
                <>
                  {result.error}
                  {result.errorCode && (
                    <div className="tiny" style={{ marginTop: 4 }}>
                      Fejlkode fra gatewayen: <code>{result.errorCode}</code>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}
